import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import './PriceComparativePage.css';
import Multiselect from 'multiselect-react-dropdown';
import searchIcon from '../assets/Icon/search_icon.svg';
import { apiService } from '../services/apiService';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-dist';

const Plot = createPlotlyComponent(Plotly);

const PriceComparativePage = () => {
  const { user } = useSelector(state => state.auth);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [historicalSalesData, setHistoricalSalesData] = useState([]);
  const [partTypes, setPartTypes] = useState([]);
  const [icNumbers, setIcNumbers] = useState([]);
  const [mileageRanges, setMileageRanges] = useState([
    //'< 100',
    '< 50,000',
    '>= 50,000 and < 75,000',
    '>= 75,000 and < 100,000',
    '>= 100,000 and < 125,000',
    '>= 125,000'
  ]);
  
  // Selected values state
  const [selectedPartType, setSelectedPartType] = useState('');
  const [selectedIcNumber, setSelectedIcNumber] = useState('');
  const [selectedMileageRanges, setSelectedMileageRanges] = useState(['All']);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Applied filters state
  const [appliedFilters, setAppliedFilters] = useState({
    partType: '',
    icNumber: '',
    mileageRanges: ['All'],
    startDate: '',
    endDate: ''
  });
  
  // Statistics state
  const [stats, setStats] = useState({
    totalRecords: 0,
    averagePrice: 0,
    minPrice: 0,
    maxPrice: 0,
    totalMileageRanges: 0
  });

  // Chart data state
  const [chartData, setChartData] = useState([]);
  const [chartLayout, setChartLayout] = useState({});
  const [showChart, setShowChart] = useState(false);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
 
  // No data message state
  const [noDataMessage, setNoDataMessage] = useState('');
  // Regions that had no valid points to plot
  const [unplottedRegions, setUnplottedRegions] = useState([]);
  // Per-region plotting summary
  const [regionPlotSummary, setRegionPlotSummary] = useState([]);
  // Canonical regions that should always be reported, even if absent in data
  const defaultRegions = useMemo(() => ['MAR', 'MWR', 'SER', 'SWR'], []);
  // Table summary state (below graph)
  const [tableColumns, setTableColumns] = useState([]);
  const [regionTableData, setRegionTableData] = useState([]);
  
  // Search summary table state
  const [SearchTableFilterData, setSearchTableFilterData] = useState([]);
  const [searchSummaryLoading, setSearchSummaryLoading] = useState(false);
  
  // IC Number search functionality
  const [icSearchTerm, setIcSearchTerm] = useState('');
  const [showIcDropdown, setShowIcDropdown] = useState(false);
  
  // Filtered IC numbers based on search term
  const filteredIcNumbers = useMemo(() => {
    if (!icSearchTerm.trim()) {
      return icNumbers;
    }
    return icNumbers.filter(icNumber => 
      icNumber.toLowerCase().includes(icSearchTerm.toLowerCase())
    );
  }, [icNumbers, icSearchTerm]);
  
  // Calculate default dates
  const defaultStartDate = useMemo(() => {
    // Fixed default start date: 2025-04-01 (shows as 01-04-2025 in UI)
    return '2025-04-01';
  }, []);

  const defaultEndDate = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Function to calculate exponential regression
  const calculateExponentialRegression = (data) => {
    if (!data || data.length < 2) return null;

    // Filter valid data points
    const validData = data.filter(point => 
      point.mileage > 0 && 
      point.price > 0 && 
      !isNaN(point.mileage) && 
      !isNaN(point.price)
    );

    if (validData.length < 2) return null;

    // For very small datasets (2-3 points), use simple linear regression
    if (validData.length <= 3) {
      console.log('Using linear regression for small dataset');
      return calculateLinearRegression(validData);
    }

    // Convert to log-linear form: ln(price) = ln(a) - b * mileage
    // This models price decay with mileage: price = a * exp(-b * mileage)
    const logPrices = validData.map(point => Math.log(point.price));
    const mileages = validData.map(point => point.mileage);

    // Calculate means
    const meanMileage = mileages.reduce((sum, val) => sum + val, 0) / mileages.length;
    const meanLogPrice = logPrices.reduce((sum, val) => sum + val, 0) / logPrices.length;

    // Calculate regression coefficients
    let numerator = 0;
    let denominator = 0;

    for (let i = 0; i < mileages.length; i++) {
      numerator += (mileages[i] - meanMileage) * (logPrices[i] - meanLogPrice);
      denominator += (mileages[i] - meanMileage) ** 2;
    }

    if (denominator === 0) return null;

    const b = -numerator / denominator; // Negative because price decreases with mileage
    const lnA = meanLogPrice + b * meanMileage; // Adjusted for negative b
    const a = Math.exp(lnA);

    console.log('Exponential regression coefficients:', { a, b, region: 'calculated' });
    return { a, b, type: 'exponential' };
  };

  // Function to calculate linear regression for small datasets
  const calculateLinearRegression = (data) => {
    const mileages = data.map(point => point.mileage);
    const prices = data.map(point => point.price);

    const meanMileage = mileages.reduce((sum, val) => sum + val, 0) / mileages.length;
    const meanPrice = prices.reduce((sum, val) => sum + val, 0) / prices.length;

    let numerator = 0;
    let denominator = 0;

    for (let i = 0; i < mileages.length; i++) {
      numerator += (mileages[i] - meanMileage) * (prices[i] - meanPrice);
      denominator += (mileages[i] - meanMileage) ** 2;
    }

    if (denominator === 0) return null;

    const slope = numerator / denominator;
    const intercept = meanPrice - slope * meanMileage;

    console.log('Linear regression coefficients:', { slope, intercept, region: 'calculated' });
    return { slope, intercept, type: 'linear' };
  };

  // Function to generate regression line points
  const generateRegressionLine = (coefficients, minMileage, maxMileage) => {
    if (!coefficients) return { x: [], y: [] };

    const points = [];
    
    // Generate more points for smoother line
    const step = Math.max(1, (maxMileage - minMileage) / 50);

    for (let mileage = minMileage; mileage <= maxMileage; mileage += step) {
      let price;
      
      if (coefficients.type === 'linear') {
        // Linear model: price = slope * mileage + intercept
        price = coefficients.slope * mileage + coefficients.intercept;
      } else {
        // Exponential decay model: price = a * exp(-b * mileage)
        price = coefficients.a * Math.exp(-coefficients.b * mileage);
      }
      
      points.push({ x: mileage, y: price });
    }

    console.log('Generated regression line points:', points);
    return {
      x: points.map(p => p.x),
      y: points.map(p => p.y)
    };
  };

  // Function to prepare chart data
  const prepareChartData = (filteredData) => {
    if (!filteredData || filteredData.length === 0) return [];

    // Group data by region
    const regionGroups = {};
    const allRegionsSet = new Set(defaultRegions);
    filteredData.forEach(item => {
      const region = item.region || 'Unknown';
      allRegionsSet.add(region);
      if (!regionGroups[region]) {
        regionGroups[region] = [];
      }
      
      // Only include valid data points
      if (item.mileage > 0 && item.all_parts_gross_sales > 0) {
        regionGroups[region].push({
          mileage: item.mileage * 1000, // Multiply by 1000 for display on graph
          price: item.all_parts_gross_sales
        });
      }
    });

    const chartTraces = [];
    const colors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];
    const regionLineMap = {};

    Object.keys(regionGroups).forEach((region, index) => {
      const regionData = regionGroups[region];
      
      if (regionData.length === 0) return;

      // Sort data by mileage
      regionData.sort((a, b) => a.mileage - b.mileage);

      // Calculate regression coefficients
      const coefficients = calculateExponentialRegression(regionData);
      
      // Get mileage range for regression line (already scaled)
      const minMileage = Math.min(...regionData.map(d => d.mileage));
      const maxMileage = Math.max(...regionData.map(d => d.mileage));

      // Create scatter plot for actual data points (markers only, no connecting lines)
      chartTraces.push({
        x: regionData.map(d => d.mileage),
        y: regionData.map(d => d.price),
        mode: 'markers',
        type: 'scatter',
        name: `${region} - Data Points`,
        marker: {
          color: colors[index % colors.length],
          size: 8,
          opacity: 0.8,
          symbol: 'x'
        },
        hovertemplate: 
          `<b>${region}</b><br>` +
          `Mileage: %{x:,.0f} miles<br>` +
          `Price: $%{y:,.2f}<br>` +
          `<extra></extra>`
      });

      // Create exponential regression line
      if (coefficients && (coefficients.a > 0 || coefficients.type === 'linear')) {
        console.log(`Creating exponential line for region: ${region}`, coefficients);
        const regressionLine = generateRegressionLine(coefficients, minMileage, maxMileage);
        
        console.log(`Exponential line data for ${region}:`, regressionLine);
        regionLineMap[region] = true;
        chartTraces.push({
          x: regressionLine.x,
          y: regressionLine.y,
          mode: 'lines',
          type: 'scatter',
          name: `${region} - Exponential Line`,
          line: {
            color: colors[index % colors.length],
            width: 3,
            dash: 'solid'
          },
          hovertemplate: 
            `<b>${region} - Exponential</b><br>` +
            `Mileage: %{x:,.0f} miles<br>` +
            `Predicted Price: $%{y:,.2f}<br>` +
            `<extra></extra>`
        });
      } else {
        console.log(`No exponential line for region: ${region}`, coefficients);
        regionLineMap[region] = false;
      }
    });

    // Determine regions with no valid points to plot
    const allRegions = Array.from(allRegionsSet);
    const regionsWithoutData = allRegions
      .filter(r => defaultRegions.includes(r))
      .filter(r => !regionGroups[r] || regionGroups[r].length === 0);
    setUnplottedRegions(regionsWithoutData);

    // Build per-region summary
    const summary = allRegions
      .filter(r => defaultRegions.includes(r) || (regionGroups[r] && regionGroups[r].length > 0))
      .map(r => ({
      region: r,
      pointsCount: (regionGroups[r] && regionGroups[r].length) ? regionGroups[r].length : 0,
      hasLine: Boolean(regionGroups[r] && regionGroups[r].length > 0 && regionLineMap[r])
    }));
    setRegionPlotSummary(summary);

    return chartTraces;
  };

  // Function to prepare chart layout
  const prepareChartLayout = (filteredData) => {
    if (!filteredData || filteredData.length === 0) {
      return {
        title: {
          text: 'Price vs Mileage Analysis',
          font: { size: 20, color: '#333' }
        },
        xaxis: {
          title: { text: 'Mileage (miles)', font: { size: 14 } },
          showgrid: true,
          zeroline: false
        },
        yaxis: {
          title: { text: 'Price ($)', font: { size: 14 } },
          showgrid: true,
          zeroline: false
        },
        plot_bgcolor: '#fff',
        paper_bgcolor: '#f8f9fa',
        margin: { t: 60, l: 60, r: 20, b: 60 },
        showlegend: true,
        legend: {
          x: 0.99,
          xanchor: 'right',
          y: 0.99,
          yanchor: 'top',
          bgcolor: 'rgba(255,255,255,0.7)',
          bordercolor: '#ccc',
          borderwidth: 1
        }
      };
    }

    // Axis ranges (data already filtered in handleSearch)
    const allMileages = filteredData.map(item => item.mileage * 1000).filter(m => m > 0); // Multiply by 1000 for display
    const allPrices = filteredData.map(item => item.all_parts_gross_sales).filter(p => p > 0);

    const minMileage = Math.min(...allMileages);
    const maxMileage = Math.max(...allMileages);
    const minPrice = Math.min(...allPrices);
    const maxPrice = Math.max(...allPrices);

    // Create title with applied filters
    let titleText = 'Price vs Mileage Analysis';
    if (selectedPartType) titleText += ` - ${selectedPartType}`;
    if (selectedIcNumber) titleText += ` (IC: ${selectedIcNumber})`;
    // if (selectedMileageRanges && !selectedMileageRanges.includes('All')) {
    //   titleText += ` [Mileage: ${selectedMileageRanges.join(', ')}]`;
    // }

    return {
      title: {
        text: titleText,
        font: { size: 20, color: '#333' }
      },
      xaxis: {
        title: { text: 'Mileage (miles)', font: { size: 14 } },
        range: [minMileage * 0.9, maxMileage * 1.1],
        showgrid: true,
        zeroline: false,
        tickformat: ',d'
      },
      yaxis: {
        title: { text: 'Price ($)', font: { size: 14 } },
        range: [minPrice * 0.9, maxPrice * 1.1],
        showgrid: true,
        zeroline: false,
        tickformat: '$,.0f'
      },
      plot_bgcolor: '#fff',
      paper_bgcolor: '#f8f9fa',
      margin: { t: 60, l: 60, r: 20, b: 60 },
      showlegend: true,
      legend: {
        x: 0.99,
        xanchor: 'right',
        y: 0.99,
        yanchor: 'top',
        bgcolor: 'rgba(255,255,255,0.7)',
        bordercolor: '#ccc',
        borderwidth: 1
      }
    };
  };

  // Fetch historical sales data on component mount
  useEffect(() => {
    const fetchHistoricalSales = async () => {
      try {
        setInitialLoading(true);
        const data = await apiService.GethistoricalSales(false);
        
        // apiService methods return the payload directly, not { data }
        const rows = Array.isArray(data) ? data : (data && data.data) ? data.data : [];
        
        if (rows && rows.length >= 0) {
          setHistoricalSalesData(rows);
          console.log('Historical Sales Data loaded:', rows);
          console.log('Sample row structure:', rows[0]);
          
          // Extract unique values for dropdowns
          const uniquePartTypes = [...new Set(rows.map(item => item.part_name || item.part_type).filter(Boolean))];
          const uniqueIcNumbers = [...new Set(rows.map(item => item.interchange_number).filter(Boolean))];
          
          setPartTypes(uniquePartTypes);
          setIcNumbers(uniqueIcNumbers);
          
          // Set "ENGINE" as default if it exists in the available part types (case-insensitive)
          const engineOption = uniquePartTypes.find(type => 
            type.toLowerCase() === 'engine' || type.toLowerCase() === 'engines'
          );
          console.log('Available part types:', uniquePartTypes);
          console.log('Found engine option:', engineOption);
          if (engineOption) {
            setSelectedPartType(engineOption);
            setAppliedFilters(prev => ({
              ...prev,
              partType: engineOption
            }));
            console.log('Set engine as default:', engineOption);
          } else {
            console.log('No engine option found in available part types');
          }
          
          console.log('Available Part Types:', uniquePartTypes);
          console.log('Available IC Numbers:', uniqueIcNumbers);
          console.log('IC Numbers array length:', uniqueIcNumbers.length);
        }
      } catch (error) {
        console.error('Error fetching historical sales data:', error);
        // Set empty data when API fails
        setHistoricalSalesData([]);
        setPartTypes([]);
        setIcNumbers([]);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchHistoricalSales();
  }, []);

  useEffect(() => {
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setAppliedFilters(prev => ({
      ...prev,
      startDate: defaultStartDate,
      endDate: defaultEndDate
    }));
  }, [defaultStartDate, defaultEndDate]);

  // Update IC numbers when part type or dates change
  useEffect(() => {
    updateICNumbersBasedOnFilters(selectedPartType, startDate, endDate);
    // Clear selected IC number when filters change
    setSelectedIcNumber('');
    setIcSearchTerm('');
    setShowIcDropdown(false);
  }, [selectedPartType, startDate, endDate, historicalSalesData]);

  // Auto-search when component loads with default "Engine" selection (only if IC selected)
  useEffect(() => {
    if (
      selectedPartType &&
      selectedPartType.toLowerCase().includes('engine') &&
      startDate &&
      endDate &&
      selectedIcNumber &&
      !initialLoading
    ) {
      handleSearch();
    }
  }, [selectedPartType, startDate, endDate, initialLoading]);

  // Set engine as default when part types are loaded
  useEffect(() => {
    if (partTypes.length > 0 && !selectedPartType) {
      const engineOption = partTypes.find(type => 
        type.toLowerCase() === 'engine' || type.toLowerCase() === 'engines'
      );
      if (engineOption) {
        console.log('Setting engine as default from partTypes:', engineOption);
        setSelectedPartType(engineOption);
        setAppliedFilters(prev => ({
          ...prev,
          partType: engineOption
        }));
      }
    }
  }, [partTypes, selectedPartType]);

  // Handle clicking outside the IC dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownContainer = document.querySelector('.searchable-select-container');
      if (dropdownContainer && !dropdownContainer.contains(event.target)) {
        setShowIcDropdown(false);
      }
    };

    if (showIcDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showIcDropdown]);

  // Function to update IC Number dropdown based on selected filters
  const updateICNumbersBasedOnFilters = (partType, startDate, endDate) => {
    if (!historicalSalesData.length) return;

    let filteredData = historicalSalesData;

    // Filter by part type if selected
    if (partType) {
      filteredData = filteredData.filter(item => 
        (item.part_name === partType) || (item.part_type === partType)
      );
    }

    // Filter by date range if dates are provided
    if (startDate && endDate) {
      filteredData = filteredData.filter(item => {
        if (!item.invoice_date) return false;
        
        // Create date objects and normalize to start of day for accurate comparison
        const invoiceDate = new Date(item.invoice_date);
        const startDateObj = new Date(startDate + 'T00:00:00');
        const endDateObj = new Date(endDate + 'T23:59:59');
        
        // Check if invoice date is within the range (inclusive)
        return invoiceDate >= startDateObj && invoiceDate <= endDateObj;
      });
    }

    // Extract unique IC numbers from filtered data
    const uniqueIcNumbers = [...new Set(filteredData.map(item => item.interchange_number).filter(Boolean))];
    setIcNumbers(uniqueIcNumbers);

    // Clear selected IC number if it's no longer in the filtered options
    if (selectedIcNumber && !uniqueIcNumbers.includes(selectedIcNumber)) {
      setSelectedIcNumber('');
    }

    console.log('Updated IC Numbers based on filters:', uniqueIcNumbers);
  };

  // Handle multiselect functions for Mileage Range only
  const handleSelect = (selectedList, selectedItem, setter) => {
    if (selectedItem === 'All') {
      setter(['All']);
    } else {
      const newList = selectedList.filter(item => item !== 'All');
      setter(newList.length > 0 ? newList : ['All']);
    }
  };

  const handleRemove = (selectedList, removedItem, setter, options) => {
    if (removedItem === 'All') {
      setter(['All']);
    } else {
      const newList = selectedList.filter(item => item !== removedItem);
      setter(newList.length > 0 ? newList : ['All']);
    }
  };

  const handleSearch = async () => {
    // Clear previous validation errors
    setValidationErrors({});
    // Clear previous no data message
    setNoDataMessage('');
    
    // Validate IC Number selection
    if (!selectedIcNumber || selectedIcNumber.trim() === '') {
      setValidationErrors(prev => ({
        ...prev,
        icNumber: 'Please select an IC Number to proceed with the search.'
      }));
      return;
    }
    
    setLoading(true);
    
    // Filter historical sales data based on selected criteria
    let filteredData = historicalSalesData;
    
    if (selectedPartType) {
      filteredData = filteredData.filter(item => 
        (item.part_name === selectedPartType) || (item.part_type === selectedPartType)
      );
    }
    
    if (selectedIcNumber) {
      filteredData = filteredData.filter(item => item.interchange_number === selectedIcNumber);
    }
    
    // Filter by date range if dates are provided
    if (startDate && endDate) {
      filteredData = filteredData.filter(item => {
        if (!item.invoice_date) return false;
        
        // Create date objects and normalize to start of day for accurate comparison
        const invoiceDate = new Date(item.invoice_date);
        const startDateObj = new Date(startDate + 'T00:00:00');
        const endDateObj = new Date(endDate + 'T23:59:59');
        
        // Check if invoice date is within the range (inclusive)
        return invoiceDate >= startDateObj && invoiceDate <= endDateObj;
      });
      
    }
    
    // Apply mileage range filter if not "All"
    // if (selectedMileageRanges && !selectedMileageRanges.includes('All')) {
    //   filteredData = filteredData.filter(item => {
    //     if (!item.mileage || item.mileage <= 0) return false;
        
    //     return selectedMileageRanges.some(range => {
    //       // Multiply API mileage by 1000 to match filter ranges
    //       const actualMileage = item.mileage * 1000;
    //       switch (range) {
    //         //case '< 100' :
    //         //return actualMileage < 100;
    //         case '< 50,000':
    //           return actualMileage < 50000;
    //         case '>= 50,000 and < 75,000':
    //           return actualMileage >= 50000 && actualMileage < 75000;
    //         case '>= 75,000 and < 100,000':
    //           return actualMileage >= 75000 && actualMileage < 100000;
    //         case '>= 100,000 and < 125,000':
    //           return actualMileage >= 100000 && actualMileage < 125000;
    //         case '>= 125,000':
    //           return actualMileage >= 125000;
    //         default:
    //           return false;
    //       }
    //     });
    //   });
    // }
    
    // Calculate statistics from filtered data
    const prices = filteredData.map(item => item.all_parts_gross_sales || 0).filter(price => price > 0);
    const totalRecords = filteredData.length;
    const averagePrice = prices.length > 0 ? prices.reduce((sum, price) => sum + price, 0) / prices.length : 0;
    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
    const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
    
    // Build table summary (per-region counts by mileage bucket)
    // const activeBuckets = (selectedMileageRanges && !selectedMileageRanges.includes('All')) ? selectedMileageRanges : mileageRanges;
    const activeBuckets = mileageRanges; // Use all mileage ranges since filter is commented out
    const bucketTest = (rangeLabel, mileage) => {
      // Multiply API mileage by 1000 to match filter ranges
      const actualMileage = mileage * 1000;
      switch (rangeLabel) {
        //case '< 100':
        //  return actualMileage < 100;
        case '< 50,000':
          return actualMileage < 50000;
        case '>= 50,000 and < 75,000':
          return actualMileage >= 50000 && actualMileage < 75000;
        case '>= 75,000 and < 100,000':
          return actualMileage >= 75000 && actualMileage < 100000;
        case '>= 100,000 and < 125,000':
          return actualMileage >= 100000 && actualMileage < 125000;
        case '>= 125,000':
          return actualMileage >= 125000;
        default:
          return false;
      }
    };
    const regionsForTable = new Set([...defaultRegions, ...filteredData.map(d => d.region || 'Unknown')]);
    const tableData = Array.from(regionsForTable).map(region => {
      const rows = filteredData.filter(d => (d.region || 'Unknown') === region);
      const bucketStats = {};
      activeBuckets.forEach(b => {
        bucketStats[b] = { count: 0, min: Infinity, max: -Infinity, searched: 0, found: false };
      });
      rows.forEach(r => {
        const mileageVal = r.mileage || 0;
        const priceVal = r.all_parts_gross_sales || 0;
        if (mileageVal > 0) {
          const matchedBucket = activeBuckets.find(lbl => bucketTest(lbl, mileageVal));
          if (matchedBucket) {
            const stat = bucketStats[matchedBucket];
            stat.searched += 1; // any row matching this bucket by mileage
            if (priceVal > 0) {
              stat.count += 1; // valid point considered for plot
              stat.min = Math.min(stat.min, priceVal);
              stat.max = Math.max(stat.max, priceVal);
            }
          }
        }
      });
      // Normalize Infinity values to 0 when no data in bucket
      activeBuckets.forEach(b => {
        const stat = bucketStats[b];
        if (stat.count === 0) {
          stat.min = 0;
          stat.max = 0;
        } else {
          stat.min = Math.round(stat.min * 100) / 100;
          stat.max = Math.round(stat.max * 100) / 100;
        }
        stat.found = stat.count > 0;
      });
      return {
        region,
        bucketStats,
        total: rows.length
      };
    });
    
    setTimeout(() => {
      setLoading(false);
      setAppliedFilters({
        partType: selectedPartType,
        icNumber: selectedIcNumber,
        mileageRanges: ['All'], // Set to All since filter is commented out
        startDate: startDate,
        endDate: endDate
      });
      
      // Set statistics based on filtered data
      setStats({
        totalRecords: totalRecords,
        averagePrice: Math.round(averagePrice * 100) / 100,
        minPrice: Math.round(minPrice * 100) / 100,
        maxPrice: Math.round(maxPrice * 100) / 100,
        totalMileageRanges: mileageRanges.length // Use all mileage ranges since filter is commented out
      });

      // Generate chart data
      const traces = prepareChartData(filteredData);
      const layout = prepareChartLayout(filteredData);
      
      setChartData(traces);
      setChartLayout(layout);
      setShowChart(traces.length > 0);
      setTableColumns(activeBuckets);
      setRegionTableData(tableData);
      
      if (traces.length === 0) {
        // const mileageText = selectedMileageRanges && !selectedMileageRanges.includes('All')
        //   ? ` and mileage in [${selectedMileageRanges.join(', ')}]`
        //   : '';
        setNoDataMessage(`No data found for Part Type "${selectedPartType || 'All'}"${selectedIcNumber ? `, IC ${selectedIcNumber}` : ''} between ${startDate} and ${endDate}.`);
      } else {
        setNoDataMessage('');
      } 
      
      console.log('Chart data generated:', traces);
      console.log('Chart layout:', layout);
    }, 500);
    
    // Fetch search summary data for the second table
    setSearchSummaryLoading(true);
    try {
      // Map part type to API codes
      let partTypeCode = '';
      if (selectedPartType && selectedPartType.toLowerCase().includes('transmission')) {
        partTypeCode = 'BA';
      } else if (selectedPartType && selectedPartType.toLowerCase().includes('engine')) {
        partTypeCode = 'AA';
      } else {
        partTypeCode = selectedPartType; // Send original value if not engine or transmission
      }
      
      const Respone = await apiService.GetSearchSummary({
        partType: partTypeCode,
        IC_number: selectedIcNumber,
        start_date: startDate,
        end_date: endDate
      });
      setSearchTableFilterData(Respone.data);
      console.log("search data", Respone.data);
    } catch (error) {
      console.error('Error fetching search summary:', error);
      // For now, set sample data if API is not available
      setSearchTableFilterData([
        { region: 'MAR', searchesMade: 32, qtyFound: 23 },
        { region: 'MWR', searchesMade: 106, qtyFound: 106 },
        { region: 'SER', searchesMade: 140, qtyFound: 140 },
        { region: 'SWR', searchesMade: 88, qtyFound: 23 }
      ]);
    } finally {
      setSearchSummaryLoading(false);
    }
  };

  const handleReset = () => {
    // Find the engine option from available part types
    const engineOption = partTypes.find(type => 
      type.toLowerCase() === 'engine' || type.toLowerCase() === 'engines'
    ) || '';
    
    setSelectedPartType(engineOption);
    setSelectedIcNumber('');
    setSelectedMileageRanges(['All']);
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setAppliedFilters({
      partType: engineOption,
      icNumber: '',
      mileageRanges: ['All'],
      startDate: defaultStartDate,
      endDate: defaultEndDate
    });
    setStats({
      totalRecords: 0,
      averagePrice: 0,
      minPrice: 0,
      maxPrice: 0,
      totalMileageRanges: 0
    });
    
    // Reset chart data
    setChartData([]);
    setChartLayout({});
    setShowChart(false);
    setUnplottedRegions([]);
    setRegionPlotSummary([]);
    
    // Reset search summary table data
    setSearchTableFilterData([]);
    setSearchSummaryLoading(false);
    
    // Reset IC search functionality
    setIcSearchTerm('');
    setShowIcDropdown(false);
    
    // Clear validation errors
    setValidationErrors({});
    // Clear no data message
    setNoDataMessage('');
  };

  if (initialLoading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px'
      }}>
        <div className="bouncing-loader">
          <div></div><div></div><div></div>
        </div>
        <p style={{ 
          color: '#333', 
          fontSize: '18px', 
          fontWeight: '600',
          margin: 0,
          textAlign: 'center',
          textShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}>
          Data is loading, please wait...
        </p>
        <style>
          {`
          .bouncing-loader {
            display: flex;
            align-items: flex-end;
            justify-content: center;
            height: 60px;
            gap: 10px;
          }
          .bouncing-loader div {
            width: 18px;
            height: 18px;
            background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
            border-radius: 50%;
            animation: bouncing-loader 0.6s infinite alternate;
            box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
          }
          .bouncing-loader div:nth-child(2) {
            animation-delay: 0.2s;
          }
          .bouncing-loader div:nth-child(3) {
            animation-delay: 0.4s;
          }
          @keyframes bouncing-loader {
            to {
              transform: translateY(-30px) scale(1.1);
              box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
            }
          }
          `}
        </style>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px'
      }}>
        <div className="bouncing-loader">
          <div></div><div></div><div></div>
        </div>
        <p style={{ 
          color: '#333', 
          fontSize: '18px', 
          fontWeight: '600',
          margin: 0,
          textAlign: 'center',
          textShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}>
          Data is loading, please wait...
        </p>
        <style>
          {`
          .bouncing-loader {
            display: flex;
            align-items: flex-end;
            justify-content: center;
            height: 60px;
            gap: 10px;
          }
          .bouncing-loader div {
            width: 18px;
            height: 18px;
            background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
            border-radius: 50%;
            animation: bouncing-loader 0.6s infinite alternate;
            box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
          }
          .bouncing-loader div:nth-child(2) {
            animation-delay: 0.2s;
          }
          .bouncing-loader div:nth-child(3) {
            animation-delay: 0.4s;
          }
          @keyframes bouncing-loader {
            to {
              transform: translateY(-30px) scale(1.1);
              box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
            }
          }
          `}
        </style>
      </div>
    );
  }

  return (
    <div className="">
      <div className="filter_section d-flex align-items-start row mb_25">
        <div className="filter_items col-md-3 m-0">
          <label className="font_14 fw-med text_black">
            Part Type
          </label>
          <select
            className="form-control w-100"
            value={selectedPartType}
            onChange={(e) => {
              setSelectedPartType(e.target.value);
              // Clear selected IC number when part type changes
              setSelectedIcNumber('');
            }}
          >
            <option value="">Select Part Type</option>
            {partTypes.map((type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div> 
        <div className="filter_items col-md-3 m-0">
          <label className="font_14 fw-med text_black">
            Start Date
          </label>
                     <input
             type="date"
             className="form-control"
             value={startDate}
             onChange={(e) => {
               setStartDate(e.target.value);
               // Clear selected IC number when date changes
               setSelectedIcNumber('');
             }}
           />
        </div>
        <div className="filter_items col-md-3 m-0">
          <label className="font_14 fw-med text_black">
            End Date
          </label>
                     <input
             type="date"
             className="form-control"
             value={endDate}
             onChange={(e) => {
               setEndDate(e.target.value);
               // Clear selected IC number when date changes
               setSelectedIcNumber('');
             }}
           />
        </div>
        <div className="filter_items col-md-3 m-0">
          <label className="font_14 fw-med text_black">
            IC Number
          </label>
          <div className="searchable-select-container">
            <div className="input-wrapper">
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search IC Number..."
                value={icSearchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setIcSearchTerm(value);
                  // If user is typing and it matches a selected value, clear the selection
                  if (selectedIcNumber && value !== selectedIcNumber) {
                    setSelectedIcNumber('');
                  }
                }}
                onFocus={() => setShowIcDropdown(true)}
                onDoubleClick={() => {
                  if (selectedIcNumber) {
                    setIcSearchTerm(selectedIcNumber);
                    setSelectedIcNumber('');
                    setShowIcDropdown(true);
                  }
                }}
                style={{ marginBottom: showIcDropdown ? '5px' : '0' }}
              />
              {(selectedIcNumber || icSearchTerm) && (
                <button
                  type="button"
                  className="clear-button"
                  onClick={() => {
                    setSelectedIcNumber('');
                    setIcSearchTerm('');
                    setShowIcDropdown(false);
                  }}
                  title="Clear selection"
                >
                  ✕
                </button>
              )}
            </div>
            {showIcDropdown && (
              <div className="searchable-dropdown">
                <div className="dropdown-list">
                  {filteredIcNumbers.length > 0 ? (
                                         filteredIcNumbers.map((icNumber, index) => (
                       <div
                         key={index}
                         className={`dropdown-item ${selectedIcNumber === icNumber ? 'selected' : ''}`}
                         onClick={() => {
                           setSelectedIcNumber(icNumber);
                           setIcSearchTerm(icNumber);
                           setShowIcDropdown(false);
                           // Clear validation error when user selects an IC Number
                           if (validationErrors.icNumber) {
                             setValidationErrors(prev => ({
                               ...prev,
                               icNumber: null
                             }));
                           }
                         }}
                       >
                         {icNumber}
                         {selectedIcNumber === icNumber && <span className="selected-indicator">✓</span>}
                       </div>
                     ))
                  ) : (
                    <div className="dropdown-item no-results">No IC Numbers found</div>
                  )}
                </div>
              </div>
            )}
          </div>
          {validationErrors.icNumber && (
            <div className="validation-error" style={{
              color: '#f44336',
              fontSize: '12px',
              marginTop: '5px',
              fontWeight: '500'
            }}>
              {validationErrors.icNumber}
            </div>
          )}
        </div>
         
        {/* <div className="filter_items col-md-3 m-0">
          <label className="font_14 fw-med text_black positoin-relative">
            Mileage Range
            {!selectedMileageRanges.includes('All') && selectedMileageRanges.length > 0 && (
              <span className="badge bg-primary ms-2 badge_container" style={{ fontSize: '10px' }}>
                {selectedMileageRanges.length} selected
              </span>
            )}
          </label>
          <Multiselect
            isObject={false}
            options={['All', ...mileageRanges]}
            selectedValues={selectedMileageRanges}
            onSelect={(selectedList, selectedItem) => {
              handleSelect(selectedList, selectedItem, setSelectedMileageRanges);
            }}
            onRemove={(selectedList, removedItem) => {
              handleRemove(selectedList, removedItem, setSelectedMileageRanges, mileageRanges);
            }}
            placeholder="Select Mileage Ranges"
          />
        </div> */}
        
        <div className="filter_button_blk mt_26 ml_10">
          <img 
            src={searchIcon} 
            alt="search" 
            className="" 
            onClick={handleSearch}
            style={{ cursor: 'pointer' }}
          />
        </div>
      </div>

      <style>
        {`
          /* Searchable dropdown styling */
          .searchable-select-container {
            position: relative;
          }
          
          .input-wrapper {
            position: relative;
            display: flex;
            align-items: center;
          }
          
          .search-input {
            width: 100%;
            border: 1px solid #ced4da;
            border-radius: 4px;
            padding: 8px 12px;
            padding-right: 35px;
            font-size: 14px;
            background-color: #fff;
            transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
          }
          
          .clear-button {
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: #6c757d;
            font-size: 16px;
            cursor: pointer;
            padding: 2px 6px;
            border-radius: 3px;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
          }
          
          .clear-button:hover {
            background-color: #e9ecef;
            color: #495057;
          }
          
          .search-input:focus {
            border-color: #80bdff;
            outline: 0;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }
          
          .searchable-dropdown {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            z-index: 1000;
            background: white;
            border: 1px solid #ced4da;
            border-radius: 4px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            max-height: 200px;
            overflow-y: auto;
          }
          
          .dropdown-list {
            padding: 0;
            margin: 0;
            list-style: none;
          }
          
          .dropdown-item {
            padding: 10px 12px;
            cursor: pointer;
            border-bottom: 1px solid #f8f9fa;
            transition: background-color 0.2s ease;
            font-size: 14px;
          }
          
          .dropdown-item:hover {
            background-color: #f8f9fa;
          }
          
          .dropdown-item.selected {
            background-color: #007bff;
            color: white;
          }
          
          .dropdown-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .selected-indicator {
            font-weight: bold;
            margin-left: 8px;
          }
          
          .dropdown-item.no-results {
            color: #6c757d;
            font-style: italic;
            cursor: default;
          }
          
          .dropdown-item.no-results:hover {
            background-color: transparent;
          }
          
          /* Scrollbar styling for dropdown */
          .searchable-dropdown::-webkit-scrollbar {
            width: 6px;
          }
          
          .searchable-dropdown::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          
          .searchable-dropdown::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          
          .searchable-dropdown::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        `}
      </style>

      {unplottedRegions && unplottedRegions.length > 0 && (
        <div className="mb_10" style={{
          background: '#fff3cd',
          border: '1px solid #ffeeba',
          color: '#856404',
          borderRadius: '6px',
          padding: '8px 12px'
        }}>
          <span className="font_14 fw-med">Regions without plotted data:</span>
          <span className="font_14 ms-2">{unplottedRegions.join(', ')}</span>
        </div>
      )}

      {/* Combined graph + table frame */}
      <div className="p-0 report_table_bg combined-frame">
        <div className="chart-pane">
          {showChart ? (
            <Plot
              data={chartData}
              layout={chartLayout}
              config={{
                displayModeBar: true,
                displaylogo: false,
                showlegend: true,
                modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d']
              }}
              style={{ width: '100%', height: '100%' }}
              useResizeHandler={true}
            />
          ) : (
            <div className="no-data" style={{ padding: '12px' }}>
              <h4 className="font_18 fw-semi mb_10">Price vs Mileage Analysis</h4>
              <p>
                {loading
                  ? "Loading chart data..."
                  : noDataMessage || "Please apply filters and click Search to view the price vs mileage analysis with exponential regression curves for each region."}
              </p>
            </div>
          )}
        </div>

        {regionTableData && regionTableData.length > 0 && (
          <div className="table-pane">
            <div className="font_14 fw-med text_black mb_5">Applied mileage results by region</div>
            <div className="table-responsive">
              <table className="table table-sm mb-0 kpi-table">
                <thead>
                  <tr>
                    <th className="font_12 align-middle" rowSpan={3}>Region</th>
                    <th className="font_12 text-center" colSpan={tableColumns.length * 3}>Applied mileage results</th>
                  </tr>
                  <tr>
                    {tableColumns.map(col => (
                      <th key={`grp-${col}`} className="font_12 text-center" colSpan={3}>{col}</th>
                    ))}
                  </tr>
                  <tr>
                    {tableColumns.map(col => (
                      <React.Fragment key={`subhdr-${col}`}>
                        <th className="font_12 text-end">Min ($)</th>
                        <th className="font_12 text-end">Max ($)</th>
                        <th className="font_12 text-end">Qty Sold</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {regionTableData.map(row => (
                    <tr key={row.region}>
                      <td className="font_12">{row.region}</td>
                      {tableColumns.map(col => {
                        const s = row.bucketStats[col] || { min: 0, max: 0, count: 0, searched: 0, found: false };
                        const minTxt = s.min ? s.min.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '-';
                        const maxTxt = s.max ? s.max.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '-';
                        return (
                          <React.Fragment key={`cells-${row.region}-${col}`}>
                            <td className="font_12 text-end">{minTxt}</td>
                            <td className="font_12 text-end">{maxTxt}</td>
                            <td className="font_12 text-end">{s.count}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <style>
              {`
                .kpi-table {
                  border-collapse: collapse;
                  width: 100%;
                  font-size: 11px;
                }
                .kpi-table th, .kpi-table td {
                  border: 1px solid #dee2e6;
                  vertical-align: middle;
                  background-clip: padding-box;
                  padding: 4px 6px;
                }
                .kpi-table thead th {
                  background-color: #f8f9fa;
                  font-weight: 600;
                }
                .combined-frame {
                  height: auto;
                  min-height: 80vh;
                  display: flex;
                  flex-direction: column;
                }
                .chart-pane {
                  flex: 0 0 auto;
                  min-height: 500px;
                }
                .table-pane {
                  flex: 0 0 auto;
                  overflow: visible;
                  padding: 8px 0 0 0;
                }
                .table-responsive {
                  overflow: visible;
                }
                .report_table_bg.combined-frame {
                  padding: 16px;
                  overflow: visible;
                }
                .report_table_bg.combined-frame .chart-pane {
                  overflow: visible;
                }
                .report_table_bg.combined-frame .table-pane {
                  overflow: visible;
                }
                .report_table_bg.combined-frame .table-responsive {
                  overflow: visible;
                }
              `}
                        </style>
          </div>
        )}
        
        {/* Second Table - Search Summary - Only show after search */}
        {appliedFilters.icNumber && (
          <div className="table-pane">
            <div className="font_14 fw-med text_black mb_5">Search Summary by Region</div>
            {searchSummaryLoading ? (
              <div className="text-center py-4">
                <div className="bouncing-loader">
                  <div></div><div></div><div></div>
                </div>
                <p className="mt-2 text-muted">Loading search summary data...</p>
              </div>
            ) : SearchTableFilterData && SearchTableFilterData?.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-sm mb-0 kpi-table">
                  <thead>
                    <tr>
                      <th className="font_12 text-center">Region</th>
                      <th className="font_12 text-center">Searches Made</th>
                      <th className="font_12 text-center">Qty Found</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SearchTableFilterData.map((row, index) => (
                      <tr key={index}>
                        <td className="font_12 text-center">{row.region || 'N/A'}</td>
                        <td className="font_12 text-center">{row.total_search || 0}</td>
                        <td className="font_12 text-center">{row.total_found || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-4 text-muted">
                <p>No search summary data found for the selected criteria</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceComparativePage;
